"use client";

import { useRef, useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";

import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { UploadsImagesWithPreview } from "@/components/common/Dropzone";
import {
  useGetPresignedURLQuery,
  useUpdateTenantBrandingMutation,
} from "@/store/slices/apiSlice";
import { uploadToS3 } from "@/utils";
import { useDispatch } from "react-redux";
import { setPrimaryColor } from "@/store/slices";
import { focusFirstEditable } from "@/utils/focusFirstEditable";

const brandColors = ["#FFFFFF", "#000000", "#9CA3AF", "#8B5CF6", "#00D3F2"];

const isValidHex = (value) => /^#([0-9A-Fa-f]{6})$/.test(value);

export default function BrandColorsDialog({
  open,
  onClose,
  logoFile,
  setLogoFile,
  primaryColor = "",
  secondaryColor = "",
  setSecondaryColor,
  tenantId,
  onSave,
}) {
  const dispatch = useDispatch();
  const [showPicker, setShowPicker] = useState(false);
  const pickerRef = useRef(null);
  const [selectedColorType, setSelectedColorType] = useState("primary");
  const [error, setError] = useState("");
  const [logoFileId, setLogoFileId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [formColors, setFormColors] = useState({
    primaryColor,
    secondaryColor,
  });

  const { data: presignedUrlData, refetch: refetchPresignedUrl } =
    useGetPresignedURLQuery(
      {
        tenantId,
        fileName: logoFile?.name,
        contentType: logoFile?.type,
      },
      {
        skip: !(logoFile instanceof File),
      },
    );

  const [updateTenantBranding, { isLoading: isSaving }] =
    useUpdateTenantBrandingMutation();

  const handleSaveBranding = async (data) => {
    try {
      const payload = {
        logoUrl: logoFileId,
        primaryColor: formColors.primaryColor,
        secondaryColor: formColors.secondaryColor,
      };

      const response = await updateTenantBranding({
        tenantId,
        payload,
      }).unwrap();
      // if (res?.response?.statusEnum === 1) {
      const latestPrimaryColor =
        response?.response?.data?.branding?.primaryColor ||
        response?.response?.data?.primaryColor ||
        payload.primaryColor;

      dispatch(
        setPrimaryColor({
          tenantId,
          primaryColor: latestPrimaryColor,
        }),
      );
      localStorage.setItem(`tenant-primary-colors-${tenantId}`, latestPrimaryColor);
      // dispatch(setPrimaryColor(latestPrimaryColor));
      // onSuccess?.(payload);
      // }

      // console.log("Branding updated:", response);
      onSave?.({
        ...response,
        primaryColor: latestPrimaryColor,
      });
      onClose?.(); // dialog close
    } catch (error) {
      console.error("Failed to update branding:", error);
    }
  };

  useEffect(() => {
    const uploadLogo = async () => {
      const uploadUrl = presignedUrlData?.response?.data?.uploadUrl;

      const objectUrl = presignedUrlData?.response?.data?.objectUrl;

      if (logoFile && uploadUrl) {
        try {
          setIsLoading(true);

          const success = await uploadToS3(logoFile, uploadUrl);

          if (!success) {
            throw new Error("S3 upload failed");
          }

          setLogoFileId(objectUrl);
        } catch (err) {
          setError("Image upload failed");
        } finally {
          setIsLoading(false);
        }
      }
    };

    uploadLogo();
  }, [logoFile, presignedUrlData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (open) {
      setFormColors({
        primaryColor,
      });

      setSelectedColorType("primary");
      setShowPicker(false);
    }
  }, [open, primaryColor]);

  const validateImageRatio = (file) => {
    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);

      img.onload = () => {
        const isSquare = img.width === img.height;

        URL.revokeObjectURL(objectUrl);
        resolve(isSquare);
      };

      img.src = objectUrl;
    });
  };

  const activeColorKey = "primaryColor";

  const activeColor = formColors[activeColorKey];

  const updateActiveColor = (value) => {
    setFormColors((prev) => ({
      ...prev,
      primaryColor: value,
    }));

    setPrimaryColor?.(value);
    setSecondaryColor?.(value);
  };

  const handleHexInputChange = (value) => {
    const cleanedValue = value.trim();

    if (!cleanedValue) {
      updateActiveColor("#");
      return;
    }

    const normalizedValue = cleanedValue.startsWith("#")
      ? cleanedValue
      : `#${cleanedValue}`;

    updateActiveColor(normalizedValue.slice(0, 7));
  };

  const handlePresetColor = (color) => {
    updateActiveColor(color);
  };
  const BrandColorInput = ({ label, value, active, onChangeClick }) => {
    return (
      <div>
        <p className="mb-3 text-sm font-bold text-neutral-100">{label}</p>

        <div className="flex h-16 overflow-hidden rounded-md border border-[#14708A] bg-[#151515]">
          <div
            className={`flex flex-1 items-center gap-4 px-4 ${
              active ? "ring-1 ring-[#00D3F2]" : ""
            }`}
          >
            <span
              className="h-9 w-9 shrink-0 rounded-full border-2 border-white"
              style={{
                backgroundColor: isValidHex(value) ? value : "transparent",
              }}
            />

            <span className="text-base font-bold text-neutral-100">
              {value || "No color selected"}
            </span>
          </div>

          <button
            type="button"
            onClick={onChangeClick}
            className="w-[120px] border-l border-[#14708A] text-sm font-bold text-[#00D3F2] hover:bg-white/5"
          >
            Change
          </button>
        </div>
      </div>
    );
  };
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose?.()}>
      <DialogContent
        className="w-[calc(100vw-32px)] max-w-[820px] max-h-[92vh] overflow-y-auto rounded-lg
            border bg-brand-19 border-brand-11 px-5 py-8 text-neutral-100 shadow-2xl sm:px-10 lg:px-14"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        // Prevent auto-focus when dialog opens
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          focusFirstEditable(e.currentTarget);
        }}
      >
        <div className="relative px-7 py-7">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#67D8FF]">
              Add Brand Colors
            </DialogTitle>
          </DialogHeader>

          <div className="mt-7 space-y-5">
            {/* Upload Logo */}
            <div>
              <UploadsImagesWithPreview
                placeholder="Upload logo"
                fileFormat="Upload logo (PNG, JPG)"
                value={logoFile}
                onChange={(val) => {
                  setLogoFile(val);
                  if (val) setError("");
                }}
                // onChange={async (val) => {
                //   if (val instanceof File) {
                //     const isValidRatio = await validateImageRatio(val);

                //     if (!isValidRatio) {
                //       setError("Only 1:1(square) ratio image is allowed.");
                //       return;
                //     }
                //   }

                //   setLogoFile(val);

                //   if (val) setError("");
                // }}
                setError={(msg) => setError(msg)}
              />

              {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
            </div>

            {/* Primary Color */}
            <div
              className={`grid grid-cols-1 gap-8 ${
                showPicker ? "lg:grid-cols-[1fr_310px]" : "lg:grid-cols-1"
              }`}
            >
              <div className="space-y-6">
                <BrandColorInput
                  label="Add Primary Color"
                  value={formColors.primaryColor}
                  active={selectedColorType === "primary"}
                  onChangeClick={() => {
                    setSelectedColorType("primary");
                    setShowPicker(true);
                  }}
                />
              </div>

              {showPicker && (
                <div className="rounded-none border border-[#006D80] bg-[#1A2236] p-5">
                  <HexColorPicker
                    color={isValidHex(activeColor) ? activeColor : "#00D3F2"}
                    onChange={updateActiveColor}
                    className="!w-full"
                  />

                  <div className="mt-4 flex items-center gap-3">
                    <span
                      className="h-9 w-9 shrink-0 rounded-md border border-white/40"
                      style={{
                        backgroundColor: isValidHex(activeColor)
                          ? activeColor
                          : "transparent",
                      }}
                    />

                    <input
                      value={activeColor}
                      onChange={(e) => handleHexInputChange(e.target.value)}
                      placeholder="#00D3F2"
                      maxLength={7}
                      className="
                                  w-full
                                  h-10
                                  flex-1
                                  rounded-md
                                  border
                                  border-[#14708A]
                                  bg-[#151515]
                                  px-3
                                  text-sm
                                  font-bold
                                  text-neutral-100
                                  outline-none
                                  placeholder:text-neutral-100/40
                                  focus:border-[#00D7F2]
                                "
                    />
                  </div>

                  {!isValidHex(activeColor) && (
                    <p className="mt-2 text-xs text-red-400">
                      Enter a valid hex color, e.g. #00D3F2
                    </p>
                  )}

                  <p className="mt-4 text-center text-sm text-neutral-100/70">
                    Brand colors:
                  </p>

                  <div className="mt-4 flex items-center justify-center gap-4">
                    {brandColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => handlePresetColor(color)}
                        className={`h-7 w-7 rounded-full border-2 ${
                          activeColor?.toLowerCase() === color.toLowerCase()
                            ? "border-white"
                            : "border-transparent"
                        }`}
                        style={{ backgroundColor: color }}
                        aria-label={`Choose ${color}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-1 gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="
                    h-8
                    w-full
                    border-[#607A9B]
                    bg-[#223957]
                    px-8
                    font-bold
                    text-neutral-100
                    hover:bg-[#2B466B]
                    hover:text-neutral-100
                    sm:w-auto
                  "
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={isSaving}
                // onClick={() =>
                //   onSave?.({
                //     logoFile,
                //     primaryColor,
                //   })
                // }
                onClick={handleSaveBranding}
                className="h-8 rounded-md bg-[#188CEB] px-5 text-xs font-bold text-white hover:bg-[#147AD0]"
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
